import { isBoolean, isNumber } from "./Utils";
import { Vector } from "../Core/Utils/Vector";
var _random = Math.random;
var easings = new Map();
export function addEasing(name, easing) {
  if (easings.get(name)) {
    return;
  }

  easings.set(name, easing);
}
export function getEasing(name) {
  return easings.get(name) || function (value) {
    return value;
  };
}
export function setRandom() {
  var rnd = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Math.random;
  _random = rnd;
}
export function getRandom() {
  return clamp(_random(), 0, 1 - 1e-16);
}
export function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}
export function mix(comp1, comp2, weight1, weight2) {
  return Math.floor((comp1 * weight1 + comp2 * weight2) / (weight1 + weight2));
}
export function randomInRange(r) {
  var max = getRangeMax(r);
  var min = getRangeMin(r);

  if (max === min) {
    min = 0;
  }

  return getRandom() * (max - min) + min;
}
export function getRangeValue(value) {
  return isNumber(value) ? value : randomInRange(value);
}
export function getRangeMin(value) {
  return isNumber(value) ? value : value.min;
}
export function getRangeMax(value) {
  return isNumber(value) ? value : value.max;
}
export function setRangeValue(source, value) {
  if (source === value || value === undefined && isNumber(source)) {
    return source;
  }

  var min = getRangeMin(source),
      max = getRangeMax(source);
  return value !== undefined ? {
    min: Math.min(min, value),
    max: Math.max(max, value)
  } : setRangeValue(min, max);
}
export function getValue(options) {
  var random = options.random,
      _ref = isBoolean(random) ? {
    enable: random,
    minimumValue: 0
  } : random,
      enable = _ref.enable,
      minimumValue = _ref.minimumValue;

  return enable ? getRangeValue(setRangeValue(options.value, minimumValue)) : getRangeValue(options.value);
}
export function getDistances(pointA, pointB) {
  var dx = pointA.x - pointB.x,
      dy = pointA.y - pointB.y;
  return {
    dx: dx,
    dy: dy,
    distance: Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2))
  };
}
export function getDistance(pointA, pointB) {
  return getDistances(pointA, pointB).distance;
}
export function getParticleDirectionAngle(direction, position, center) {
  if (isNumber(direction)) {
    return direction * Math.PI / 180;
  }

  switch (direction) {
    case "top":
      return -Math.PI / 2;

    case "top-right":
      return -Math.PI / 4;

    case "right":
      return 0;

    case "bottom-right":
      return Math.PI / 4;

    case "bottom":
      return Math.PI / 2;

    case "bottom-left":
      return 3 * Math.PI / 4;

    case "left":
      return Math.PI;

    case "top-left":
      return -3 * Math.PI / 4;

    case "inside":
      return Math.atan2(center.y - position.y, center.x - position.x);

    case "outside":
      return Math.atan2(position.y - center.y, position.x - center.x);

    default:
      return getRandom() * Math.PI * 2;
  }
}
export function getParticleBaseVelocity(direction) {
  var baseVelocity = Vector.origin;
  baseVelocity.length = 1;
  baseVelocity.angle = direction;
  return baseVelocity;
}
export function collisionVelocity(v1, v2, m1, m2) {
  return Vector.create(v1.x * (m1 - m2) / (m1 + m2) + v2.x * 2 * m2 / (m1 + m2), v1.y);
}
export function calcPositionFromSize(data) {
  return data.position && data.position.x !== undefined && data.position.y !== undefined ? {
    x: data.position.x * data.size.width / 100,
    y: data.position.y * data.size.height / 100
  } : undefined;
}
export function calcPositionOrRandomFromSize(data) {
  var _data$position$x, _data$position, _data$position$y, _data$position2;

  return {
    x: ((_data$position$x = (_data$position = data.position) === null || _data$position === void 0 ? void 0 : _data$position.x) !== null && _data$position$x !== void 0 ? _data$position$x : getRandom() * 100) * data.size.width / 100,
    y: ((_data$position$y = (_data$position2 = data.position) === null || _data$position2 === void 0 ? void 0 : _data$position2.y) !== null && _data$position$y !== void 0 ? _data$position$y : getRandom() * 100) * data.size.height / 100
  };
}
export function calcPositionOrRandomFromSizeRanged(data) {
  var _data$position3, _data$position4;

  var position = {
    x: ((_data$position3 = data.position) === null || _data$position3 === void 0 ? void 0 : _data$position3.x) !== undefined ? getRangeValue(data.position.x) : undefined,
    y: ((_data$position4 = data.position) === null || _data$position4 === void 0 ? void 0 : _data$position4.y) !== undefined ? getRangeValue(data.position.y) : undefined
  };
  return calcPositionOrRandomFromSize({
    size: data.size,
    position: position
  });
}
export function calcExactPositionOrRandomFromSize(data) {
  var _data$position$x2, _data$position5, _data$position$y2, _data$position6;

  return {
    x: (_data$position$x2 = (_data$position5 = data.position) === null || _data$position5 === void 0 ? void 0 : _data$position5.x) !== null && _data$position$x2 !== void 0 ? _data$position$x2 : getRandom() * data.size.width,
    y: (_data$position$y2 = (_data$position6 = data.position) === null || _data$position6 === void 0 ? void 0 : _data$position6.y) !== null && _data$position$y2 !== void 0 ? _data$position$y2 : getRandom() * data.size.height
  };
}
export function calcExactPositionOrRandomFromSizeRanged(data) {
  var _data$position7, _data$position8;

  var position = {
    x: ((_data$position7 = data.position) === null || _data$position7 === void 0 ? void 0 : _data$position7.x) !== undefined ? getRangeValue(data.position.x) : undefined,
    y: ((_data$position8 = data.position) === null || _data$position8 === void 0 ? void 0 : _data$position8.y) !== undefined ? getRangeValue(data.position.y) : undefined
  };
  return calcExactPositionOrRandomFromSize({
    size: data.size,
    position: position
  });
}
export function parseAlpha(input) {
  return input ? input.endsWith("%") ? parseFloat(input) / 100 : parseFloat(input) : 1;
}